/**
 * WIP
 * Base global store implementation that abstracts away api/caching/rehydration
 * for resources manipulated via API and UI.
 * @example
 * // will call callback every time data is made available (via cache, network,
 * // or setting explicitly in front-end code)
 * foobarStore.subscribe(foobar => doSomethingWith(foobar));
 */

import stableStringify from 'json-stable-stringify';
import isPlainObject from 'lodash/isPlainObject';

import {reporter} from '../error-reporting';
import {BaseClient, QueryOptions} from '../queries/utils/base-client';
import {deleteFromFetchCache} from '../queries/utils/fetch-cache';
import {removeByValue} from 'common/util/array';
import {ResponseError} from 'common/util/api-client';

type TransformType = {
  /**
   * transform GET API response into this.value
   */
  index?: Function;
  /**
   * transform POST/PUT API response into a {k, v} map. v will be returned out of POST/PUT
   * and the store will patch {[k], v} in to this.value
   */
  update?: Function;
};

export interface StoreOptions<Value> {
  apiClient: BaseClient;

  transform?: TransformType;

  defaultValue?: Partial<Value>;
}

interface Subscription {
  key: string;

  params: object;
  options: object;

  initialized: boolean;
  callbacks: SubscriptionCallback[];
}

interface SubscriptionCallbackParams {
  cached?: boolean;
  error?: boolean;
  fromUpdate?: boolean;
}

type SubscriptionCallback = (args: SubscriptionCallbackParams) => void;

/**
 * Factory for creating a BaseClient from a given URL to facilitate
 * easy Store usage until apiClient is migrated to use ApiClient directly
 *
 * @param url network resource for the generated client
 */
export function createSimpleClient(url: string): BaseClient {
  return new (class extends BaseClient {
    public buildEndpoint() {
      return url;
    }
  })();
}

export class Store<Value> {
  static getKey(params = {}, options = {}) {
    return stableStringify({params, options});
  }

  public value: Value;

  private apiClient = null;
  private transform: TransformType = {};

  private subscriptions = new Map<string, Subscription>();

  constructor(options: StoreOptions<Value>) {
    this.apiClient = options.apiClient;
    if (options.transform) {
      this.transform = options.transform;
    }
    if (options.defaultValue) {
      this.value = options.defaultValue as Value;
    }
  }

  async fetch(params = {}, options = {}, key = ``) {
    const subscription = this.getSubscription(key);
    if (subscription.initialized) {
      // give an update if we have initialized
      this.fireCallbacks(key);
    } else {
      subscription.params = params;
      subscription.options = options;

      await this.fetchForSubscription(subscription, {fromFetchCache: true});
      await this.fetchForSubscription(subscription, {updateFetchCache: true});
    }
  }

  subscribe(callback, key = ``) {
    this.getSubscription(key).callbacks.push(callback);
  }

  unsubscribe(callback, keys = ``) {
    this.subscriptions.get(keys).callbacks = removeByValue(this.subscriptions.get(keys).callbacks, callback);
  }

  fireCallbacks(key = ``, meta: SubscriptionCallbackParams = {}) {
    for (const callback of this.getSubscription(key).callbacks) {
      callback(meta);
    }
  }

  async post(params, key = ``) {
    const result = await this.write(params, {method: `POST`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async put(itemKey, params, key = ``) {
    const result = await this.write(params, {method: `PUT`}, key, itemKey);
    this.invalidateFetchCache();
    return result;
  }

  async patch(params, key = ``) {
    const result = await this.write(params, {method: `PATCH`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async delete(itemKey, params, key = ``) {
    await this.apiClient.query(params, {clearCache: true, method: `DELETE`});
    delete this.value[itemKey];
    this.invalidateFetchCache();

    this.fireCallbacks(key);
  }

  /**
   * Performs a partial update for object like updates and a value
   * replacement for other type of updates
   *
   * @param valueUpdate update to patch or replace the store value
   */
  _update(valueUpdate: Partial<Value> = {}) {
    if (Array.isArray(this.value)) {
      if (!Array.isArray(valueUpdate)) {
        throw new Error(`valueUpdate isn't an array but the store is`);
      }
      // the 'as unknown' is to work about the following:
      // TS2352: Conversion of type 'any[]' to type 'Value' may be a mistake because
      // neither type sufficiently overlaps with the other. If this was intentional,
      // convert the expression to 'unknown' first.
      this.value = (valueUpdate as unknown) as Value;
    } else if (isPlainObject(valueUpdate)) {
      // if it's an object, we support partial update
      this.value = {...this.value, ...valueUpdate};
    } else {
      this.value = valueUpdate as Value;
    }
  }

  update(valueUpdate: Partial<Value>, key = ``) {
    if (Array.isArray(valueUpdate) || Object.keys(valueUpdate).length > 0) {
      this._update(valueUpdate);
      this.fireCallbacks(key, {fromUpdate: true});
    }
  }

  private getSubscription(key) {
    let subscription = this.subscriptions.get(key);
    if (!subscription) {
      subscription = {
        key,
        params: null,
        options: null,
        initialized: false,
        callbacks: [],
      };
    }
    this.subscriptions.set(key, subscription);
    return subscription;
  }

  private invalidateFetchCache() {
    for (const subscription of this.subscriptions.values()) {
      if (subscription.initialized && subscription.params && subscription.options) {
        deleteFromFetchCache(
          this.apiClient.getUrl(subscription.params),
          this.apiClient.getFetchOptions(subscription.params, subscription.options),
        );
      }
    }
  }

  // TODO: consolidate `fetchForSubscription` and `write`. so far there is some divergence
  // - `fetchForSubscription` is catching and reporting errors to rollbar, whereas clients of `post` and
  // `put` expect and need error information to display on the UI
  // - the `index` and the `update` transform functions have different return formats
  // - `fetchForSubscription` sets this.initialized
  private async fetchForSubscription(subscription, extraOptions: QueryOptions) {
    try {
      const result = await this.apiClient.query(subscription.params, {...subscription.options, ...extraOptions});
      if (result) {
        this._update(
          this.transform.index ? this.transform.index(result, subscription.params, subscription.options) : result,
        );
        subscription.initialized = true;
        this.fireCallbacks(subscription.key, {cached: true});
      }
    } catch (err) {
      reporter.error(err);
      this.fireCallbacks(subscription.key, {error: true});
    }
  }

  private async write(params, options: QueryOptions, key = ``, itemKey = null) {
    const result = await this.apiClient.query(params, options);
    if (result.status === `error`) {
      throw new ResponseError(result);
    }

    // 'itemKey' isn't technically necessary as client of `put` knows the itemKey and puts it into
    // `params`, but we add it to the `put` interface to mirror the `delete` interface
    const {k, v} = await (this.transform.update ? this.transform.update(result, itemKey) : result);
    this._update({[k]: v} as Value);
    this.fireCallbacks(key);
    return v;
  }

  // TODO async write (to api), invalidate
}
